import { useEffect, useMemo, useRef, useState } from "react";
import { buildPlan, downloadTemplate, FIELD_LABELS, parseWorkbook } from "./importParser";
import { commitPlan } from "./importCommit";

const ACTION_LABEL = { create: "New", update: "Update", skip: "Skip" };
const PREVIEW_LIMIT = 100;

export default function ImportMembersModal({ members, payments, period, categories, onClose, onImported }) {
  const [stage, setStage] = useState("pick"); // pick | preview | importing
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [newStatus, setNewStatus] = useState("Active");
  const [showSkipped, setShowSkipped] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && stage !== "importing" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, stage]);

  const plan = useMemo(
    () => (parsed ? buildPlan(parsed, { members, payments, categories, updateExisting, newStatus }) : null),
    [parsed, members, payments, categories, updateExisting, newStatus]
  );

  const readFile = async (file) => {
    if (!file) return;
    setError("");
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      return setError("Please choose an Excel (.xlsx, .xls) or .csv file.");
    }
    try {
      const buffer = await file.arrayBuffer();
      const result = parseWorkbook(buffer);
      setParsed(result);
      setFileName(file.name);
      setStage("preview");
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not read that file.");
    }
  };

  const doImport = async () => {
    setStage("importing");
    setError("");
    setProgress({ done: 0, total: plan.counts.create + plan.counts.update });
    try {
      const res = await commitPlan(plan.items, period, (done, total) => setProgress({ done, total }));
      onImported(`Import finished: ${res.created} added, ${res.updated} updated`);
    } catch (err) {
      console.error(err);
      setError("The import stopped part-way. Press Refresh to see what was saved, then try again. Rows already saved will show as “Already up to date”.");
      setStage("preview");
    }
  };

  const changeFile = () => {
    setParsed(null);
    setStage("pick");
    setError("");
  };

  const importCount = plan ? plan.counts.create + plan.counts.update : 0;
  const visible = plan
    ? plan.items.filter((i) => showSkipped || i.action !== "skip").slice(0, PREVIEW_LIMIT)
    : [];
  const hiddenCount = plan
    ? plan.items.filter((i) => showSkipped || i.action !== "skip").length - visible.length
    : 0;
  const recognised = parsed
    ? Object.keys(parsed.mapping).map((k) => FIELD_LABELS[k]).filter(Boolean)
    : [];

  return (
    <div className="na-overlay" onMouseDown={stage === "importing" ? undefined : onClose}>
      <div className="na-modal na-modal-wide" onMouseDown={(e) => e.stopPropagation()}>
        <h2>Import members from Excel</h2>

        {stage === "pick" && (
          <>
            <p className="na-muted">
              Upload a spreadsheet and the register is filled from it. You'll see a preview and can check
              everything before anything is saved.
            </p>

            <div
              className={`na-drop ${dragging ? "is-drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                readFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            >
              <strong>Drop your Excel file here</strong>
              <span>or click to choose one (.xlsx, .xls, .csv)</span>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={(e) => {
                  readFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </div>

            {error && <p className="na-error">{error}</p>}

            <div className="na-tips">
              <p><strong>Not sure how to lay it out?</strong></p>
              <button type="button" className="na-btn" onClick={downloadTemplate}>Download the template</button>
              <ul>
                <li>The first row holds column titles. Only a <b>name</b> is required.</li>
                <li>People already on the register are matched by <b>membership number</b>, then <b>email</b>, and updated. Others are added.</li>
                <li>Empty cells never erase information that is already saved.</li>
                <li>Columns like <b>Payment status</b>, <b>Amount</b> and <b>Date paid</b> are saved for <b>{period}</b>, the period selected on the register.</li>
              </ul>
            </div>
          </>
        )}

        {stage !== "pick" && plan && (
          <>
            <p className="na-muted">
              <strong>{fileName}</strong> · sheet “{parsed.sheetName}” · {plan.items.length} rows with data
            </p>

            <div className="na-chips">
              <span className="na-chip na-chip-create"><b>{plan.counts.create}</b> new</span>
              <span className="na-chip na-chip-update"><b>{plan.counts.update}</b> to update</span>
              <span className="na-chip na-chip-skip"><b>{plan.counts.skip}</b> skipped</span>
              {plan.hasPaymentColumns && (
                <span className="na-chip na-chip-pay"><b>{plan.counts.payments}</b> payments for {period}</span>
              )}
            </div>

            <p className="na-muted na-small">
              Columns understood: {recognised.join(", ") || "none"}.
              {parsed.unmapped.length > 0 && <> Ignored: {parsed.unmapped.join(", ")}.</>}
            </p>

            {plan.unknownCategories.length > 0 && (
              <p className="na-warn">
                Unrecognised categories: {plan.unknownCategories.join(", ")}. They'll be saved as typed but won't
                appear under the category filter. Fix them in the sheet, or press Back.
              </p>
            )}

            <div className="na-options">
              <label>
                <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
                Update people already on the register
              </label>
              <label>
                Status for new members without one
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </label>
              <label>
                <input type="checkbox" checked={showSkipped} onChange={(e) => setShowSkipped(e.target.checked)} />
                Show skipped rows
              </label>
            </div>

            <div className="na-preview">
              <table className="na-table">
                <thead>
                  <tr><th>Row</th><th>Action</th><th>Name</th><th>Membership no.</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan="5" className="na-empty">Nothing to show.</td></tr>
                  )}
                  {visible.map((i) => (
                    <tr key={i.rowNum}>
                      <td>{i.rowNum}</td>
                      <td><span className={`na-act na-act-${i.action}`}>{ACTION_LABEL[i.action]}</span></td>
                      <td>{i.name}</td>
                      <td>{i.number || "—"}</td>
                      <td className="na-small">
                        {i.action === "skip" && i.reason}
                        {i.action === "update" && `Changes: ${i.changedKeys.join(", ")}`}
                        {i.action === "create" && (i.payment ? `Payment: ${i.payment.status}` : "New member")}
                        {i.notes.length > 0 && <span className="na-note"> · {i.notes.join("; ")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hiddenCount > 0 && <p className="na-muted na-pad-sm">…and {hiddenCount} more rows not shown here.</p>}
            </div>
          </>
        )}

        {stage === "importing" && (
          <div className="na-progress-import">
            <div className="na-progress-bar">
              <span
                className="na-seg na-seg-paid"
                style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
              />
            </div>
            <p className="na-muted">Saving {progress.done} of {progress.total}. Please keep this window open…</p>
          </div>
        )}

        {stage === "preview" && error && <p className="na-error">{error}</p>}

        {stage !== "pick" && (
          <div className="na-modal-actions">
            <button type="button" className="na-btn" onClick={changeFile} disabled={stage === "importing"}>Back</button>
            <button
              type="button"
              className="na-btn na-btn-primary"
              onClick={doImport}
              disabled={stage === "importing" || importCount === 0}
            >
              {importCount === 0 ? "Nothing to import" : `Import ${importCount} ${importCount === 1 ? "record" : "records"}`}
            </button>
          </div>
        )}

        {stage === "pick" && (
          <div className="na-modal-actions">
            <button type="button" className="na-btn" onClick={onClose}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
